// Worklist filter sheet (phone): search · handler · stage · DPD · flags · status tag · sort. Filters apply immediately.
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BottomSheet, SectionTitle } from "@/components/phone/PhoneBits";
import { cn } from "@/lib/utils";
import { cur, rl } from "@/lib/regionFmt";

const EMPTY = { search: "", handler: "", stage: "", dpd_bucket: "", manual_status: "", capital_at_risk: "", bs_protected: "" };

export function activeFilterCount(filter) {
  return ["search", "handler", "stage", "dpd_bucket", "manual_status", "capital_at_risk", "bs_protected"].filter((k) => filter[k]).length;
}

function Chip({ active, onClick, children, testid, tone = "" }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={cn("h-9 px-3 rounded-full border text-[13px] font-medium whitespace-nowrap sf-tap", active ? "bg-slate-900 text-white border-slate-900" : `bg-white border-slate-300 text-slate-700 ${tone}`)}>
      {children}
    </button>
  );
}

export function PhoneWorklistFilters({ open, onClose, filter, setFilter, sort, setSort, handlers, stages, byStage, byHandler, byDpdBucket, dpdBuckets, accountStatuses, capitalAtRiskCount, bsProtectedCount, resultCount }) {
  const selectedHandlers = filter.handler ? filter.handler.split(",").filter(Boolean) : [];
  const toggleHandler = (h) => setFilter((f) => {
    const list = f.handler ? f.handler.split(",").filter(Boolean) : [];
    const next = list.includes(h) ? list.filter((x) => x !== h) : [...list, h];
    return { ...f, handler: next.join(",") };
  });
  const handlerOptions = [...new Set(["Unassigned", ...handlers, ...Object.keys(byHandler).filter((h) => h && h !== "Unassigned")])];

  return (
    <BottomSheet open={open} onClose={onClose} title="Filter the worklist" testid="phone-worklist-filters"
      footer={(
        <div className="flex items-center gap-2 pt-1">
          <Button variant="outline" className="h-11 flex-1" onClick={() => setFilter(EMPTY)} data-testid="phone-filters-clear"><X className="w-4 h-4 mr-1" /> Clear all</Button>
          <Button className="h-11 flex-1" onClick={onClose} data-testid="phone-filters-done">Show {resultCount} {resultCount === 1 ? "case" : "cases"}</Button>
        </div>
      )}>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={filter.search || ""} onChange={(e) => setFilter((f) => ({ ...f, search: e.target.value }))} placeholder="Customer name or SF#…" className="pl-9 h-11 text-base" data-testid="phone-filter-search" />
      </div>

      <SectionTitle>Handler</SectionTitle>
      <div className="max-h-44 overflow-y-auto rounded-lg border divide-y">
        <label className="flex items-center gap-3 h-11 px-3 text-sm sf-tap" data-testid="phone-filter-handler-all">
          <Checkbox checked={selectedHandlers.length === 0} onCheckedChange={() => setFilter((f) => ({ ...f, handler: "" }))} className="h-5 w-5" />
          <span className="flex-1 font-medium">All handlers</span>
        </label>
        {handlerOptions.map((h) => (
          <label key={h} className="flex items-center gap-3 h-11 px-3 text-sm sf-tap" data-testid={`phone-filter-handler-${h}`}>
            <Checkbox checked={selectedHandlers.includes(h)} onCheckedChange={() => toggleHandler(h)} className="h-5 w-5" />
            <span className="flex-1 truncate">{h}</span>
            <span className="text-slate-500 tabular-nums text-xs">{byHandler[h] || 0}</span>
          </label>
        ))}
      </div>

      <SectionTitle>Stage</SectionTitle>
      <div className="flex gap-2 flex-wrap">
        {stages.map((s) => (
          <Chip key={s.key} active={filter.stage === s.key} onClick={() => setFilter((f) => ({ ...f, stage: f.stage === s.key ? "" : s.key }))} testid={`phone-filter-stage-${s.key}`}>
            {s.label} <span className="opacity-70 tabular-nums ml-1">{byStage[s.key] || 0}</span>
          </Chip>
        ))}
        {capitalAtRiskCount > 0 && <Chip active={!!filter.capital_at_risk} onClick={() => setFilter((f) => ({ ...f, capital_at_risk: f.capital_at_risk ? "" : "1" }))} testid="phone-filter-car" tone="border-orange-300 text-orange-900">Capital at Risk <span className="opacity-70 ml-1">{capitalAtRiskCount}</span></Chip>}
        {bsProtectedCount > 0 && <Chip active={!!filter.bs_protected} onClick={() => setFilter((f) => ({ ...f, bs_protected: f.bs_protected ? "" : "1" }))} testid="phone-filter-bs" tone="border-purple-300 text-purple-900">{rl("hold")} <span className="opacity-70 ml-1">{bsProtectedCount}</span></Chip>}
      </div>

      <SectionTitle>Days past due</SectionTitle>
      <div className="flex gap-2 flex-wrap">
        {dpdBuckets.map((b) => (
          <Chip key={b.key} active={filter.dpd_bucket === b.key} onClick={() => setFilter((f) => ({ ...f, dpd_bucket: f.dpd_bucket === b.key ? "" : b.key }))} testid={`phone-filter-dpd-${b.key}`}>
            {b.label} <span className="opacity-70 tabular-nums ml-1">{byDpdBucket[b.key] || 0}</span>
          </Chip>
        ))}
      </div>

      <SectionTitle>Status tag</SectionTitle>
      <Select value={filter.manual_status || "all"} onValueChange={(v) => setFilter((f) => ({ ...f, manual_status: v === "all" ? "" : v }))}>
        <SelectTrigger className="h-11 text-base" data-testid="phone-filter-status"><SelectValue placeholder="Status tag" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All status tags</SelectItem>
          {accountStatuses.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
        </SelectContent>
      </Select>

      <SectionTitle>Sort</SectionTitle>
      <div className="grid grid-cols-2 gap-2">
        <Select value={sort.by} onValueChange={(v) => setSort((s) => ({ ...s, by: v }))}>
          <SelectTrigger className="h-11 text-base" data-testid="phone-sort-by"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="days_past_due">Days past due</SelectItem>
            <SelectItem value="arrears_amount">Arrears {cur()}</SelectItem>
            <SelectItem value="balance_amount">Balance {cur()}</SelectItem>
            <SelectItem value="customer_name">Customer name</SelectItem>
            <SelectItem value="updated_at">Last update</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sort.dir} onValueChange={(v) => setSort((s) => ({ ...s, dir: v }))}>
          <SelectTrigger className="h-11 text-base" data-testid="phone-sort-dir"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="desc">Highest first</SelectItem>
            <SelectItem value="asc">Lowest first</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </BottomSheet>
  );
}
